import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  FRAMEWORK_DISCLOSURE_COPY,
  NAV_LABELS,
  OWASP_MAPPING_LABELS,
  STATUS_LABELS,
  normalizeSecurityStatus,
} from '@/lib/vocabulary/securityTerminology';
import { formatTimestampSafe, getTimestampMeta } from '@/lib/utils/formatTimestamp';
import {
  CONTROL_ALIASES,
  getControlRegistryEntry,
  isKnownControlKey,
  resolveControlKey,
} from '@/lib/controlRegistry';

const formatControlName = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const badgeMeta = (badge: 'covered' | 'partial' | 'action' | 'planned') => {
  switch (badge) {
    case 'covered':
      return { label: STATUS_LABELS.covered, variant: 'success' as const };
    case 'action':
      return { label: STATUS_LABELS.actionRequired, variant: 'danger' as const };
    case 'planned':
      return { label: STATUS_LABELS.monitoringPlanned, variant: 'neutral' as const };
    default:
      return { label: STATUS_LABELS.partial, variant: 'warning' as const };
  }
};

const formatTimestamp = (value?: string | null) => {
  const meta = getTimestampMeta(value);
  return { label: meta.label, title: meta.title || 'Not reported by site yet' };
};

const formatAge = (valueMs?: number | null) => {
  if (!valueMs || valueMs <= 0) return '—';
  const minutes = Math.floor(valueMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
};

const CONTROL_COPY: Record<string, { meaning: string; shadowScan: string; action: string }> = {
  mfa_coverage_snapshot: {
    meaning: 'Tracks how many admins have enrolled MFA.',
    shadowScan: 'Monitors MFA coverage reported by the connector.',
    action: 'Enroll remaining admins in MFA.',
  },
  admin_geo_guard: {
    meaning: 'Tracks where admin access originates and whether allow/deny rules are active.',
    shadowScan: 'Monitors admin access locations and policy changes from the connector.',
    action: 'Manage allowed locations in Admin Access Location Rules.',
  },
  autoupdate_plugins_detect: {
    meaning: 'Checks whether plugin auto-updates are enabled.',
    shadowScan: 'Monitors update posture and policy signals.',
    action: 'Enable plugin auto-updates (allowlist recommended).',
  },
  // Source: plugin canonical copy (password policy control).
  password_policy_enforced: {
    meaning: 'Enforces minimum password length when passwords are changed or reset. Optional breached-password blocking.',
    shadowScan: 'This control applies only when a user changes or resets a password. It does not force periodic password changes and does not run during login.',
    action: 'Breached-password blocking checks the chosen password against a breach database when enabled.',
  },
};

const GROUPS = [
  {
    id: 'account_access',
    title: 'Identity & Access',
    description: 'Controls that reduce account takeover and privileged access abuse.',
    controlKeys: [
      'mfa_coverage_snapshot',
      'password_policy_enforced',
      'admin_geo_guard',
    ],
    owaspIds: ['A01', 'A07'],
  },
  {
    id: 'updates_integrity',
    title: 'Updates & Integrity Protection',
    description: 'Update posture, known vulnerabilities, and integrity signals.',
    controlKeys: [
      'autoupdate_plugins_detect',
    ],
    owaspIds: ['A06', 'A08'],
  },
  {
    id: 'application_hardening',
    title: 'Application Hardening',
    description: 'Configuration and hardening safeguards.',
    controlKeys: [
      'a05_file_editor_enabled',
      'a05_debug_enabled',
      'a02_missing_security_headers',
      'a02_https_not_enforced',
      'a02_insecure_cookies',
      'a03_db_error_leak',
    ],
    owaspIds: ['A02', 'A03', 'A05'],
  },
  {
    id: 'network_request',
    title: 'Network & Request Protection',
    description: 'Inbound and outbound request controls.',
    controlKeys: [
      'a10_unrestricted_http_requests',
      'a10_open_redirect_proxy_endpoints',
    ],
    owaspIds: ['A10'],
  },
  {
    id: 'visibility_evidence',
    title: 'Visibility & Evidence',
    description: 'Logging, monitoring, and evidence readiness.',
    controlKeys: [
      'visibility.evidence',
    ],
    owaspIds: ['A09'],
  },
];

const getControlCopy = (controlKey: string, groupTitle: string) => {
  const normalizedKey = resolveControlKey(controlKey);
  const copy = CONTROL_COPY[normalizedKey];
  if (copy) return copy;
  return {
    meaning: `${groupTitle} signal from recent events.`,
    shadowScan: 'Monitors and reports changes as they occur.',
    action: 'Review recommended actions and apply updates if needed.',
  };
};

const isEnforcementAvailable = (controlKey: string, enforced?: boolean | null) => {
  if (typeof enforced === 'boolean') return true;
  return controlKey.includes('enforce') || controlKey.includes('block') || controlKey.includes('rate_limit');
};

export default function DomainCoverage() {
  const { domain, managedSite } = useOutletContext<{ domain: { id: string; domain_name: string }; managedSite?: { last_heartbeat_at?: string | null } }>();
  const [filters, setFilters] = useState({
    actionRequired: false,
    enforcementAvailable: false,
    staleOffline: false,
    recentlyChanged: false,
  });

  const logsQuery = useQuery({
    queryKey: ['domain-coverage-logs', domain.id],
    queryFn: async () => {
      const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('domain_logs')
        .select('category, owasp_id, control_key, status, recommended_action, evidence, last_checked_at, created_at, enforced')
        .eq('domain_id', domain.id)
        .gte('created_at', cutoff)
        .or('owasp_id.not.is.null,category.eq.security_control')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const coverage = useMemo(() => {
    const logs = logsQuery.data || [];
    const latestByControl = new Map<string, any>();
    const duplicates = new Map<string, number>();
    const unknownKeys = new Set<string>();

    logs.forEach((log) => {
      if (!log.control_key) return;
      const normalizedKey = resolveControlKey(log.control_key);
      if (!isKnownControlKey(normalizedKey)) {
        unknownKeys.add(log.control_key);
        return;
      }
      const current = latestByControl.get(normalizedKey);
      const currentCount = duplicates.get(normalizedKey) ?? 0;
      duplicates.set(normalizedKey, currentCount + 1);
      if (!current || log.created_at > current.created_at) {
        latestByControl.set(normalizedKey, { ...log, control_key: normalizedKey });
      }
    });

    const flatControls = Array.from(latestByControl.values());

    if (import.meta.env.DEV) {
      const duplicateKeys = Array.from(duplicates.entries()).filter(([, count]) => count > 1);
      if (duplicateKeys.length > 0 || unknownKeys.size > 0) {
        console.warn('Control registry drift detected', {
          duplicates: duplicateKeys,
          unknownKeys: Array.from(unknownKeys),
          aliases: CONTROL_ALIASES,
        });
      }
    }

    const groups = GROUPS.map((group) => {
      const controls = Array.from(latestByControl.values()).filter((control) => {
        if (control.owasp_id && group.owaspIds.includes(control.owasp_id)) return true;
        return group.controlKeys.includes(control.control_key);
      });

      let groupStatus: 'covered' | 'partial' | 'action' | 'planned' = 'planned';
      let lastChecked = '';
      let enforcementAvailable = 0;
      let enforcementActive = 0;

      controls.forEach((control) => {
        const normalized = normalizeSecurityStatus({ status: control.status, enforced: control.enforced, hasSignals: true });
        if (normalized.badge === 'action') groupStatus = 'action';
        else if (normalized.badge === 'partial' && groupStatus !== 'action') groupStatus = 'partial';
        else if (normalized.badge === 'covered' && groupStatus === 'planned') groupStatus = 'covered';

        const candidate = control.last_checked_at || control.created_at;
        if (candidate && candidate > lastChecked) lastChecked = candidate;

        if (isEnforcementAvailable(control.control_key, control.enforced)) {
          enforcementAvailable += 1;
          if (control.enforced) enforcementActive += 1;
        }
      });

      const monitoringCount = controls.length;

      if (monitoringCount === 0) {
        groupStatus = 'planned';
      }

      return {
        ...group,
        controls,
        groupStatus,
        lastChecked,
        monitoringCount,
        enforcementAvailable,
        enforcementActive,
      };
    });

    const lastCheckin = flatControls.reduce<string>((latest, control) => {
      const candidate = control.last_checked_at || control.created_at || '';
      if (!candidate) return latest;
      return candidate > latest ? candidate : latest;
    }, '');

    const actionRequiredCount = flatControls.reduce((count, control) => {
      const normalized = normalizeSecurityStatus({ status: control.status, enforced: control.enforced, hasSignals: true });
      return normalized.badge === 'action' ? count + 1 : count;
    }, 0);

    const monitoringActive = flatControls.length;
    const enforcementAvailableTotal = flatControls.filter((control) => isEnforcementAvailable(control.control_key, control.enforced)).length;
    const enforcementActiveTotal = flatControls.filter((control) => isEnforcementAvailable(control.control_key, control.enforced) && control.enforced).length;

    return {
      groups,
      flatControls,
      lastCheckin,
      actionRequiredCount,
      monitoringActive,
      enforcementAvailableTotal,
      enforcementActiveTotal,
    };
  }, [logsQuery.data]);

  const overallStatus = useMemo((): 'covered' | 'partial' | 'action' | 'planned' => {
    const statuses = coverage.groups.map((group) => group.groupStatus);
    if (statuses.includes('action' as typeof statuses[number])) return 'action';
    if (statuses.includes('partial' as typeof statuses[number])) return 'partial';
    if (statuses.includes('covered' as typeof statuses[number])) return 'covered';
    return 'planned';
  }, [coverage.groups]);

  const overallBadge = badgeMeta(overallStatus);
  const canonicalHeartbeat = managedSite?.last_heartbeat_at || coverage.lastCheckin || null;
  const lastCheckinMeta = formatTimestamp(canonicalHeartbeat);
  const lastCheckinAge = canonicalHeartbeat ? Date.now() - new Date(canonicalHeartbeat).getTime() : null;
  const freshness = lastCheckinAge == null
    ? 'offline'
    : lastCheckinAge < 15 * 60 * 1000
      ? 'fresh'
      : lastCheckinAge < 2 * 60 * 60 * 1000
        ? 'stale'
        : 'offline';

  const summaryHealth = coverage.actionRequiredCount > 0 ? 'Needs attention' : 'Healthy';

  const toggleFilter = (key: keyof typeof filters) => {
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const applyFilters = (controls: any[]) => {
    return controls.filter((control) => {
      const normalized = normalizeSecurityStatus({ status: control.status, enforced: control.enforced, hasSignals: true });
      const enforcementAvailable = isEnforcementAvailable(control.control_key, control.enforced);
      const lastSeen = control.last_checked_at || control.created_at || null;
      const lastSeenMs = lastSeen ? new Date(lastSeen).getTime() : null;
      const hasTelemetry = !!lastSeenMs && !Number.isNaN(lastSeenMs) && lastSeenMs > 946684800000;
      const isStale = !hasTelemetry || (Date.now() - lastSeenMs) > 2 * 60 * 60 * 1000;
      const isRecent = lastSeenMs && (Date.now() - lastSeenMs) < 24 * 60 * 60 * 1000;

      if (filters.actionRequired && normalized.badge !== 'action') return false;
      if (filters.enforcementAvailable && !enforcementAvailable) return false;
      if (filters.staleOffline && !isStale) return false;
      if (filters.recentlyChanged && !isRecent) return false;
      return true;
    });
  };

  const sortControls = (controls: any[]) => {
    const score = (control: any) => {
      const normalized = normalizeSecurityStatus({ status: control.status, enforced: control.enforced, hasSignals: true });
      const enforcementAvailable = isEnforcementAvailable(control.control_key, control.enforced);
      if (normalized.badge === 'action') return 0;
      if (enforcementAvailable && !control.enforced) return 1;
      if (normalized.badge === 'partial') return 2;
      if (normalized.badge === 'planned') return 3;
      return 4;
    };
    return [...controls].sort((a, b) => score(a) - score(b));
  };

  return (
    <Card className="border-border/60">
      <CardHeader>
        <CardTitle>{NAV_LABELS.protectionCoverage}</CardTitle>
        <CardDescription>{FRAMEWORK_DISCLOSURE_COPY}</CardDescription>
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>Security health</span>
            <Badge variant={overallBadge.variant}>{summaryHealth}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span>Monitoring active</span>
            <Badge variant="outline">{coverage.monitoringActive}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span>Enforcement active</span>
            <Badge variant="outline">{coverage.enforcementActiveTotal}</Badge>
            <span>Policy tier: —</span>
          </div>
          <div className="flex items-center gap-2">
            <span title={lastCheckinMeta.title}>Last check-in: {lastCheckinMeta.label}</span>
            <Badge variant="outline">
              {freshness === 'fresh' ? 'Fresh' : freshness === 'stale' ? 'Stale' : 'Offline'} · {formatAge(lastCheckinAge ?? null)}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2 text-xs">
          <button
            type="button"
            className={`rounded-full border px-3 py-1 ${filters.actionRequired ? 'border-red-500 text-red-600' : 'border-border text-muted-foreground'}`}
            onClick={() => toggleFilter('actionRequired')}
          >
            Action required
          </button>
          <button
            type="button"
            className={`rounded-full border px-3 py-1 ${filters.enforcementAvailable ? 'border-amber-500 text-amber-600' : 'border-border text-muted-foreground'}`}
            onClick={() => toggleFilter('enforcementAvailable')}
          >
            Enforcement available
          </button>
          <button
            type="button"
            className={`rounded-full border px-3 py-1 ${filters.staleOffline ? 'border-slate-500 text-slate-700' : 'border-border text-muted-foreground'}`}
            onClick={() => toggleFilter('staleOffline')}
          >
            Stale/offline
          </button>
          <button
            type="button"
            className={`rounded-full border px-3 py-1 ${filters.recentlyChanged ? 'border-emerald-500 text-emerald-600' : 'border-border text-muted-foreground'}`}
            onClick={() => toggleFilter('recentlyChanged')}
          >
            Recently changed
          </button>
        </div>
        <Accordion type="multiple" className="space-y-2">
          {coverage.groups.map((group) => {
            const badge = badgeMeta(group.groupStatus);
            const controls = sortControls(applyFilters(group.controls));
            return (
              <AccordionItem key={group.id} value={group.id} className="border border-border/60 rounded-md px-3">
                <AccordionTrigger className="flex items-center justify-between text-sm">
                  <div className="flex flex-col items-start">
                    <span className="font-medium">{group.title}</span>
                    <span className="text-xs text-muted-foreground">
                      {group.description}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      <span title={formatTimestamp(group.lastChecked).title}>
                        Last checked {formatTimestamp(group.lastChecked).label}
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                    <span className="text-xs text-muted-foreground">
                      Monitoring: {group.monitoringCount} · Enforcement available: {group.enforcementAvailable} · Active: {group.enforcementActive}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  {controls.length === 0 ? (
                    <div className="text-xs text-muted-foreground">{STATUS_LABELS.monitoringPlanned}.</div>
                  ) : (
                    <div className="space-y-2">
                      {controls.map((control) => {
                        const normalized = normalizeSecurityStatus({ status: control.status, enforced: control.enforced, hasSignals: true });
                        const controlBadge = badgeMeta(normalized.badge);
                        const copy = getControlCopy(control.control_key, group.title);
                        const registryEntry = getControlRegistryEntry(control.control_key);
                        const controlTitle = registryEntry?.title ?? formatControlName(control.control_key);
                        const evidence = control.evidence ?? {};
                        const lastSeenValue = control.last_checked_at || control.created_at || null;
                        const lastSeenMeta = formatTimestamp(lastSeenValue);
                        const lastSeenMs = lastSeenValue ? new Date(lastSeenValue).getTime() : null;
                        const hasTelemetry = !!lastSeenMs && !Number.isNaN(lastSeenMs) && lastSeenMs > 946684800000;
                        const isStale = hasTelemetry && (Date.now() - lastSeenMs) > 2 * 60 * 60 * 1000;
                        const enforcementAvailable = isEnforcementAvailable(control.control_key, control.enforced);
                        const reasonBucket = (() => {
                          if (!hasTelemetry) return 'No recent telemetry yet';
                          if (isStale) return 'Telemetry stale';
                          if (normalized.badge === 'action' && enforcementAvailable && !control.enforced) return 'Needs approval';
                          if (control.control_key.includes('abuse') || control.control_key.includes('failed_login') || control.control_key.includes('rate_limit')) {
                            return 'Detected abuse';
                          }
                          return 'Misconfiguration';
                        })();
                        const nextAction = control.recommended_action || copy.action;
                        const passwordPolicyMeta = control.control_key === 'password_policy_enforced'
                          ? {
                              enforcedOnChange: evidence.enforced_on_change ?? true,
                              minimumLength: typeof evidence.min_length === 'number' ? evidence.min_length : 12,
                              minimumLengthEnabled: Boolean(evidence.enforce_min_length ?? evidence.minimum_length_enforced),
                              breachBlocking: evidence.breached_password_check
                                ? String(evidence.breached_password_check)
                                : (evidence.enforce_breached ? 'enabled' : 'disabled'),
                              lastChangeAt: evidence.last_password_change_at,
                              failedAttempts: evidence.failed_policy_attempts,
                            }
                          : null;
                        return (
                          <div key={control.control_key} className="rounded-md border border-border/50 bg-muted/10 p-3">
                            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                              <div className="flex flex-col gap-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-sm font-medium">{controlTitle}</span>
                                  <Badge variant={controlBadge.variant}>{controlBadge.label}</Badge>
                                  {normalized.badge === 'action' && (
                                    <Badge variant="outline">{reasonBucket}</Badge>
                                  )}
                                  {typeof control.enforced === 'boolean' && (
                                    <Badge variant={control.enforced ? 'success' : 'neutral'}>
                                      {control.enforced ? 'Enforced' : 'Not enforced'}
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-xs text-muted-foreground">Why: {copy.meaning}</div>
                                <div className="text-xs text-muted-foreground">Next action: {nextAction}</div>
                              </div>
                              <div className="text-xs text-muted-foreground">
                      <span title={lastSeenMeta.title}>Last seen: {lastSeenMeta.label}</span>
                              </div>
                            </div>
                            <details className="mt-2 text-xs text-muted-foreground">
                              <summary className="cursor-pointer">View details</summary>
                              <div className="mt-2 space-y-1">
                                <div>What ShadowScan does: {copy.shadowScan}</div>
                                <div>What you can do: {copy.action}</div>
                                {passwordPolicyMeta && (
                                  <>
                                    <div>Enforced on change/reset: {passwordPolicyMeta.enforcedOnChange ? 'true' : 'false'}</div>
                                    <div>
                                      Minimum length (admins): {passwordPolicyMeta.minimumLengthEnabled ? passwordPolicyMeta.minimumLength : 'Disabled'}
                                    </div>
                                    <div>Breached-password blocking: {passwordPolicyMeta.breachBlocking}</div>
                                    <div>
                                      Last password change: {passwordPolicyMeta.lastChangeAt ? formatTimestampSafe(passwordPolicyMeta.lastChangeAt * 1000) : '—'}
                                    </div>
                                    {typeof passwordPolicyMeta.failedAttempts === 'number' && (
                                      <div>Recent blocked attempts: {passwordPolicyMeta.failedAttempts}</div>
                                    )}
                                  </>
                                )}
                                {control.recommended_action && (
                                  <div>Recommended: {control.recommended_action}</div>
                                )}
                              </div>
                            </details>
                          </div>
                        );
                      })}
                      <details className="text-xs text-muted-foreground">
                        <summary className="cursor-pointer">{OWASP_MAPPING_LABELS.technicalHeading}</summary>
                        <div className="mt-2 space-y-1">
                          <div>{OWASP_MAPPING_LABELS.internalOnly}</div>
                          <div>Mapped categories: {group.owaspIds.join(', ') || '—'}</div>
                          <div>Control keys: {group.controls.map((control) => control.control_key).join(', ') || '—'}</div>
                        </div>
                      </details>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </CardContent>
    </Card>
  );
}
